// ============================================================================
// MÓDULO: LABORATÓRIO DE PEÇAS & MENTORIA (V1.0 - OAB 2ª FASE)
// ARQUIVO: src/components/MentorshipArea.jsx
// OBJETIVO: Gestão de peças práticas, uploads de rascunhos e feedback.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { auth } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { fetchMentorshipDrafts, submitMentorshipDraft } from '../services/MentorshipAreaRepository';
import { 
    PenTool, FileText, Upload, CheckCircle, Clock, 
    MessageSquare, Download, ChevronRight, AlertCircle, Award 
} from 'lucide-react';

const MentorshipArea = () =>
 {
    const [tab, setTab] = useState('WORKSHOP'); // WORKSHOP, MY_DRAFTS, MODELS
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    
    // Formulário de Simulado
    const [draft, setDraft] = useState({ title: '', content: '', category: 'PETICAO_INICIAL' });

    useEffect(() => {
        loadSubmissions();
    }, [user]);

    const loadSubmissions = async () => {
        if (!user) return setLoading(false);
        try {
            const drafts = await fetchMentorshipDrafts(user.uid);
            setSubmissions(drafts);
        } catch (e) { console.error("Erro mentoria:", e); } 
        finally { setLoading(false); }
    };

    const handleSubmitDraft = async () => {
        if (!draft.title || !draft.content) return alert("Preencha o título e o corpo da peça.");
        setUploading(true);
        try {
            await submitMentorshipDraft(user.uid, {
                ...draft,
                studentName: user.displayName || 'Aluno',
                studentEmail: user.email,
            });
            alert("✅ Peça enviada para correção! O Dr. Rodrigo analisará em breve.");
            setDraft({ title: '', content: '', category: 'PETICAO_INICIAL' });
            setTab('MY_DRAFTS');
            loadSubmissions();
        } catch (e) { alert("Erro: " + e.message); }
        finally { setUploading(false); }
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'CORRECTED': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-[9px] font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3"/> CORRIGIDA</span>;
            case 'REVIEWING': return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-[9px] font-bold flex items-center gap-1"><Clock className="w-3 h-3"/> EM ANÁLISE</span>;
            default: return <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-[9px] font-bold flex items-center gap-1"><Clock className="w-3 h-3"/> AGUARDANDO</span>;
        }
    };

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-fade-in">
            <header className="mb-10 border-b border-gray-200 pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#C5A059] text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest shadow-sm">2ª Fase OAB</span>
                        <span className="text-gray-300">|</span>
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Mentoria Individual</span>
                    </div>
                    <h2 className="text-4xl font-serif font-bold text-[#20243B] tracking-tight">Laboratório de Peças</h2>
                    <p className="text-gray-500 text-sm mt-2 max-w-xl">
                        Treine a redação jurídica com casos reais. Submeta as suas peças para correção personalizada.
                    </p>
                </div>
                
                <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200">
                    <button onClick={() => setTab('WORKSHOP')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'WORKSHOP' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>SIMULADO</button>
                    <button onClick={() => setTab('MY_DRAFTS')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'MY_DRAFTS' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>MINHAS PEÇAS</button>
                    <button onClick={() => setTab('MODELS')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'MODELS' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>MODELOS</button>
                </div>
            </header>

            <div className="min-h-[500px]">
                {tab === 'WORKSHOP' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
                                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                    <PenTool className="w-5 h-5 text-[#C5A059]"/> Redacção da Peça
                                </h3>
                                <div className="space-y-4">
                                    <input 
                                        className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-brand-blue outline-none font-bold"
                                        placeholder="Título do Simulado (Ex: Peça 01 - Habeas Corpus)"
                                        value={draft.title}
                                        onChange={e => setDraft({...draft, title: e.target.value})}
                                    />
                                    <select 
                                        className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none"
                                        value={draft.category}
                                        onChange={e => setDraft({...draft, category: e.target.value})}
                                    >
                                        <option value="PETICAO_INICIAL">Petição Inicial</option>
                                        <option value="CONTESTACAO">Contestação</option>
                                        <option value="RECURSO">Recurso Ordinário / Apelação</option>
                                        <option value="HABEAS_CORPUS">Habeas Corpus</option>
                                        <option value="MEMORIAIS">Memoriais / Alegações Finais</option>
                                    </select>
                                    <textarea 
                                        className="w-full p-6 border border-gray-200 rounded-xl text-sm min-h-[400px] font-serif leading-relaxed focus:border-brand-blue outline-none bg-gray-50/30"
                                        placeholder="Comece a redigir a sua peça aqui..."
                                        value={draft.content}
                                        onChange={e => setDraft({...draft, content: e.target.value})}
                                    />
                                    <div className="flex justify-end pt-4">
                                        <button 
                                            onClick={handleSubmitDraft}
                                            disabled={uploading}
                                            className="bg-[#20243B] text-white px-10 py-4 rounded-xl font-bold uppercase text-xs tracking-widest shadow-xl hover:bg-opacity-90 transition-all flex items-center gap-2 transform active:scale-95 disabled:opacity-50"
                                        >
                                            {uploading ? <Clock className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                                            SUBMETER PARA CORRECÇÃO
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-1 space-y-6">
                            <div className="bg-[#1a1e30] p-6 rounded-2xl text-white shadow-xl">
                                <h4 className="font-bold text-sm uppercase tracking-widest text-[#C5A059] mb-4 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4"/> Critérios FGV
                                </h4>
                                <ul className="space-y-4 text-xs text-gray-300">
                                    <li className="flex gap-3"><div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center shrink-0">1</div> <strong>Endereçamento:</strong> Indicar corretamente a autoridade competente.</li>
                                    <li className="flex gap-3"><div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center shrink-0">2</div> <strong>Fundamentação:</strong> Citar os artigos de lei e súmulas pertinentes.</li>
                                    <li className="flex gap-3"><div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center shrink-0">3</div> <strong>Pedidos:</strong> Clareza nos requerimentos de mérito.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 'MY_DRAFTS' && (
                    <div className="space-y-4 animate-fade-in">
                        {loading ? (
                            <div className="flex justify-center py-20 opacity-20"><Clock className="w-10 h-10 animate-spin"/></div>
                        ) : submissions.length === 0 ? (
                            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl p-20 text-center">
                                <PenTool className="w-12 h-12 text-gray-300 mx-auto mb-4"/>
                                <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Nenhuma submissão realizada.</p>
                            </div>
                        ) : (
                            submissions.map(sub => (
                                <div key={sub.id} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:border-brand-blue transition-all group">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 group-hover:bg-blue-50 group-hover:text-brand-blue transition-colors">
                                                <FileText className="w-6 h-6"/>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 group-hover:text-brand-blue transition-colors">{sub.title}</h4>
                                                <p className="text-[10px] text-gray-400 font-mono mt-1 uppercase">
                                                    {sub.createdAt?.toDate ? sub.createdAt.toDate().toLocaleDateString('pt-BR') : 'Data Recente'} • {sub.category}
                                                </p>
                                            </div>
                                        </div>
                                        {getStatusBadge(sub.status)}
                                    </div>
                                    {sub.feedback && (
                                        <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-xl">
                                            <div className="flex items-center gap-2 mb-2 text-green-800 font-bold text-xs uppercase tracking-wider">
                                                <Award className="w-4 h-4"/> Feedback do Professor
                                            </div>
                                            <p className="text-sm text-green-700 leading-relaxed italic">"{sub.feedback}"</p>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {tab === 'MODELS' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
                        {[
                            { name: 'Petição Inicial Cível', desc: 'Estrutura completa com fundamentação em dano moral.', id: 'm1' },
                            { name: 'Habeas Corpus Repressivo', desc: 'Modelo técnico para pedidos de liberdade.', id: 'm2' }
                        ].map(model => (
                            <div key={model.id} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl transition-all group cursor-pointer">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center"><FileText className="w-5 h-5"/></div>
                                    <Download className="w-4 h-4 text-gray-300 group-hover:text-brand-blue"/>
                                </div>
                                <h4 className="font-bold text-gray-800 mb-2">{model.name}</h4>
                                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{model.desc}</p>
                                <div className="mt-6 flex justify-end">
                                    <span className="text-[10px] font-black text-[#20243B] uppercase group-hover:underline">Baixar Modelo</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
export default MentorshipArea;